/*
 * Fig & Bloom - High Floral Design lead time.
 *
 *   node tests/hfd-lead-time.test.js
 *
 * High Floral Design pieces are made to order, so they can't go out same day.
 * The rule:
 *
 *  - the earliest delivery date is the SECOND business day after today, in the
 *    delivery location's own time zone. Today itself never counts.
 *  - a business day is Monday to Friday and not one of the location's blocked
 *    dates (public holidays and closures, `selectedBlockedDates` in the Delivery
 *    Date app). Weekends never count, even where the location delivers on them.
 *  - the 1pm same-day cutoff plays no part. Monday at 11:59 pm gives Wednesday;
 *    Tuesday at 00:05 gives Thursday. Friday, Saturday and Sunday all give Tuesday.
 *
 * Which products: everything in the `high-floral-design` collection. The layout
 * renders their ids into window.fbHfdConfig (layout/theme.liquid), along with the
 * product ids already in the cart and the product page's own id.
 *
 * ONE DATE PER CART. An order holding an HFD piece has one delivery date, and it
 * has to satisfy the rule. So the rule applies to the whole cart, not only to the
 * HFD line:
 *   - the popup calendar starts at the HFD date whenever the add, the product page
 *     or the cart involves an HFD piece (assets/delivery-suburb-popup.js)
 *   - add to cart refuses a date that is too early, clears it and reopens the date
 *     step: product-form.js for cards and the editorial page, and the capture-phase
 *     click guard below for the Delivery Date app's product-page button
 *   - alignCart() moves every dated line in a cart that holds an HFD piece onto one
 *     date: the chosen date (the ISODate cookie) when it meets the rule, otherwise
 *     the latest HFD line's date. It runs after every cart change and when the
 *     popup saves a date, so a bouquet added for today and an HFD piece added for
 *     Wednesday both end up on Wednesday.
 *
 * THE HFD PAGE. On the high-floral-design collection (and an HFD product page)
 * the calendar opened from the date pill only offers valid dates, so a customer
 * who picks a date there never has to pick again at Reserve.
 *
 * THE CART PAGE. A too-soon HFD line can still reach the cart (a cart left open
 * overnight, a pre-sale add). Every checkout path goes through /cart (the drawer's
 * button routes there, and "Buy it now" is hidden on HFD products), so the cart
 * page disables checkout and the express buttons, says why, and offers "Change
 * delivery date": the HFD collection with the popup open, then back to the cart.
 *
 * "AVAILABLE FROM …". An HFD card never says same-day (assets/fb-delivery-chip.js
 * skips it). When the chosen date is too soon, every HFD card and the HFD page's
 * Reserve controls say "Available from Thursday, 1st October" instead, via the
 * slots snippets/fb-hfd-available.liquid renders; with no date chosen, nothing.
 *
 * This is a storefront rule only. /checkout can still be reached directly, so it
 * stops the customer choosing an early date; it cannot stop a hand-edited cart.
 *
 * Loaded on every page (layout/theme.liquid), because the popup script is lazy
 * and the cart can change from any page.
 */
(function (root) {
  'use strict';

  var BUSINESS_DAYS = 2;
  var DEFAULT_TIMEZONE = 'Australia/Sydney';
  var YMD_RE = /^(\d{4})-(\d{2})-(\d{2})/;

  function config() {
    return root.fbHfdConfig || {};
  }

  function businessDays() {
    var n = parseInt(config().businessDays, 10);
    return n > 0 ? n : BUSINESS_DAYS;
  }

  function productIds() {
    return (config().productIds || []).map(function (id) { return String(id); });
  }

  function isHfdProduct(productId) {
    if (productId === null || productId === undefined || productId === '') return false;
    return productIds().indexOf(String(productId)) !== -1;
  }

  // Whether the cart holds an HFD piece. Seeded from the page render, then kept
  // current by every cart read below.
  var cartHas = null;
  function cartHasHfd() {
    if (cartHas === null) {
      cartHas = (config().cartProductIds || []).some(isHfdProduct);
    }
    return cartHas;
  }

  function cartHoldsHfd(cart) {
    return !!cart && (cart.items || []).some(function (line) {
      return isHfdProduct(line.product_id);
    });
  }

  // The product page is an HFD piece, or the page is the HFD collection itself:
  // a date chosen there is for HFD, so the calendar only offers valid ones.
  function pageIsHfd() {
    return isHfdProduct(config().pageProductId) || config().pageIsHfdCollection === true;
  }

  /*
   * Whether the rule governs the date being chosen or used now. productId is the
   * product being added, when there is one; the page (an HFD product page or the
   * HFD collection) only counts when nothing more specific is being added, so an
   * ordinary bouquet added from an HFD page keeps its normal dates.
   */
  function applies(productId) {
    if (cartHasHfd()) return true;
    if (productId !== null && productId !== undefined && productId !== '') return isHfdProduct(productId);
    return pageIsHfd();
  }

  // ---- dates ---------------------------------------------------------------

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  // "2026-09-30" from "2026-09-30", "2026-09-30T00:00:00+10:00" or any other
  // format moment can read in the location's zone. null when unreadable.
  function toYmd(value, timezone) {
    if (!value) return null;
    var s = String(value);
    var m = YMD_RE.exec(s);
    if (m) return m[1] + '-' + m[2] + '-' + m[3];
    if (root.moment && root.moment.tz) {
      var parsed = root.moment.tz(s, timezone || DEFAULT_TIMEZONE);
      if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
    }
    return null;
  }

  // Today's date in the given zone. `now` is injectable for tests.
  function todayYmd(timezone, now) {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || DEFAULT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now || new Date());
    var get = function (type) {
      for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return parts[i].value;
      return '';
    };
    return get('year') + '-' + get('month') + '-' + get('day');
  }

  /*
   * The Nth business day after `today` (a "YYYY-MM-DD" string). Pure calendar
   * arithmetic in UTC, so the machine's own zone can't shift a day.
   */
  function earliestYmd(today, blockedYmds, n) {
    var m = YMD_RE.exec(today || '');
    if (!m) return null;
    var blocked = {};
    (blockedYmds || []).forEach(function (d) { if (d) blocked[d] = true; });
    var count = n > 0 ? n : BUSINESS_DAYS;
    var d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    var found = 0;
    var guard = 0;
    while (found < count && guard++ < 366) {
      d.setUTCDate(d.getUTCDate() + 1);
      var ymd = d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
      var dow = d.getUTCDay();
      if (dow >= 1 && dow <= 5 && !blocked[ymd]) found++;
    }
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  }

  /*
   * The earliest HFD date for a location's date settings (the Delivery Date app's
   * DatePickerSettings shape: timezone, selectedBlockedDates).
   */
  function earliestForSettings(settings, now, days) {
    var tz = (settings && settings.timezone) || DEFAULT_TIMEZONE;
    var blocked = ((settings && settings.selectedBlockedDates) || [])
      .map(function (d) { return toYmd(d, tz); });
    return earliestYmd(todayYmd(tz, now), blocked, days === undefined ? businessDays() : days);
  }

  // The same settings the popup reads, for a location key ("Sydney").
  function settingsFor(location) {
    if (!location) return null;
    if (typeof root.fbGetLocationDateSettings === 'function') {
      var s = root.fbGetLocationDateSettings(location);
      if (s) return s;
    }
    var legacy = root[location + 'DatePickerArray'];
    return (legacy && legacy.DatePickerSettings && legacy.DatePickerSettings[0]) || null;
  }

  function locationForPostcode(postcode) {
    if (!postcode) return null;
    if (typeof root.fbResolvePostcode === 'function') {
      var loc = root.fbResolvePostcode(postcode);
      if (loc) return loc;
    }
    if (typeof root.findPostcodeLocation === 'function') {
      var legacy = root.findPostcodeLocation(postcode);
      if (legacy && legacy !== 'Unknown Location') return legacy;
    }
    return null;
  }

  // Always the location's own settings, never a product override, so the popup,
  // the add-to-cart gates and the cart all agree on the date.
  function earliestForLocation(location, now) {
    return earliestForSettings(settingsFor(location), now);
  }

  function earliestForPostcode(postcode, now) {
    return earliestForLocation(locationForPostcode(postcode), now);
  }

  // Whether a chosen date ("YYYY-MM-DD…") is before the HFD earliest date.
  function isTooEarly(isoDate, postcode, now) {
    var chosen = toYmd(isoDate);
    if (!chosen) return false;
    return chosen < earliestForPostcode(postcode, now);
  }

  // "Wednesday, 30th September" for the notice.
  function describe(ymd) {
    var m = YMD_RE.exec(ymd || '');
    if (!m) return '';
    var d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var months = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
      'August', 'September', 'October', 'November', 'December'];
    var day = d.getUTCDate();
    var suffix = (day % 100 >= 11 && day % 100 <= 13) ? 'th'
      : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
    return days[d.getUTCDay()] + ', ' + day + suffix + ' ' + months[d.getUTCMonth()];
  }

  function notice(ymd) {
    var n = businessDays();
    return 'High Floral Design pieces need ' + n + ' business days’ notice. '
      + 'The earliest delivery is ' + describe(ymd) + '.';
  }

  // ---- one date per cart ---------------------------------------------------

  function readCookie(name) {
    var nameEQ = name + '=';
    var parts = (root.document ? root.document.cookie : '').split(';');
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].replace(/^\s+/, '');
      if (c.indexOf(nameEQ) === 0) {
        var v = c.substring(nameEQ.length);
        try { return decodeURIComponent(v); } catch (e) { return v; }
      }
    }
    return null;
  }

  function writeCookie(name, value, days) {
    if (!root.document) return;
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    root.document.cookie = name + '=' + (value || '') + '; expires=' + date.toUTCString() + '; path=/';
    // One copy per name (snippets/fb-delivery-date-guard.liquid).
    if (root.fbDeliveryDate && root.fbDeliveryDate.settle) root.fbDeliveryDate.settle(name);
  }

  // A line that carries a delivery date.
  function isDatedLine(line) {
    var p = line && line.properties;
    if (!p || !p._ISODate) return false;
    return true;
  }

  /*
   * The date a cart holding an HFD piece should be on, and its label, or null when
   * the cart needs nothing. Pure: `cookies` is { iso, label, postcode }.
   */
  function targetDate(cart, cookies, now) {
    if (!cartHoldsHfd(cart)) return null;
    var hfdLines = (cart.items || []).filter(function (line) {
      return isDatedLine(line) && isHfdProduct(line.product_id);
    });
    if (!hfdLines.length) return null;

    if (cookies && cookies.iso && cookies.label && !isTooEarly(cookies.iso, cookies.postcode, now)) {
      return { iso: cookies.iso, label: cookies.label, fromCookie: true };
    }
    var latest = hfdLines.reduce(function (best, line) {
      return toYmd(line.properties._ISODate) > toYmd(best.properties._ISODate) ? line : best;
    });
    // Never settle the cart on a date the rule forbids. A cart like that (from
    // before the rule, or another tab) is left for the customer to re-date.
    if (isTooEarly(latest.properties._ISODate, cookies && cookies.postcode, now)) return null;
    return {
      iso: latest.properties._ISODate,
      label: latest.properties['Delivery Date'] || '',
      fromCookie: false
    };
  }

  // The line changes that put every dated line on the target date.
  function lineChanges(cart, target) {
    if (!target) return [];
    var want = toYmd(target.iso);
    return (cart.items || []).filter(function (line) {
      return isDatedLine(line) && toYmd(line.properties._ISODate) !== want;
    }).map(function (line) {
      var props = {};
      Object.keys(line.properties).forEach(function (k) { props[k] = line.properties[k]; });
      props['Delivery Date'] = target.label;
      props._ISODate = target.iso;
      return { id: line.key, quantity: line.quantity, properties: props };
    });
  }

  // HFD lines whose date is now too soon (a cart left open overnight, a pre-sale
  // add, a navigation mid-update). Pure. Judged by the chosen postcode (the
  // cookie), the same one the popup and alignCart use, so the guard can never
  // disagree with the fix; a line's own Postcode only stands in when there is none.
  function tooEarlyHfdLines(cart, postcode, now) {
    return ((cart && cart.items) || []).filter(function (line) {
      if (!isDatedLine(line) || !isHfdProduct(line.product_id)) return false;
      return isTooEarly(line.properties._ISODate, postcode || line.properties.Postcode, now);
    });
  }

  function joinNames(names) {
    if (names.length <= 1) return names.join('');
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  // Where the cart page's "Change delivery date" button goes: the HFD collection,
  // which opens the popup at the date step and comes back to the cart once a date
  // is saved (see the redate handling at the bottom of this file).
  var REDATE_PARAM = 'hfd_redate';
  var REDATE_URL = '/collections/high-floral-design?' + REDATE_PARAM + '=1';

  function isCartPage() {
    return !!root.location && /^\/cart\/?$/.test(root.location.pathname || '');
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /*
   * The cart page's last line of defence. While an HFD line's date is too soon:
   * Secure Checkout is disabled and marked data-hfd-blocked (the cart footer's
   * guards treat that like its drinks rule, sections/main-cart-footer.liquid),
   * the express checkout buttons are hidden, and a notice explains why with a
   * button to choose a new date. Everything is put back once the date is fixed.
   * Resolves nothing; safe to call with any cart.
   */
  function guardCartPage(cart, now, pending) {
    if (!root.document || !isCartPage()) return;
    var doc = root.document;
    var postcode = readCookie('deliveryPostcode');
    var bad = tooEarlyHfdLines(cart, postcode, now);
    var button = doc.getElementById('checkout');
    var express = doc.querySelectorAll('.cart__dynamic-checkout-buttons');
    var existing = doc.getElementById('fbHfdCartNotice');

    if (!bad.length && !pending) {
      if (button && button.hasAttribute('data-hfd-blocked')) {
        button.removeAttribute('data-hfd-blocked');
        // Only lift what this guard put there: the drinks rule and an empty
        // cart still keep the button disabled.
        button.disabled = button.hasAttribute('data-ratio-blocked')
          || button.hasAttribute('data-centrepiece-blocked')
          || button.hasAttribute('data-date-blocked')
          || !(cart && cart.item_count !== 0 && (cart.items || []).length);
      }
      // Other delivery guards keep express checkout hidden until they release it.
      if (!(button && (button.hasAttribute('data-date-blocked') || button.hasAttribute('data-centrepiece-blocked')))) {
        Array.prototype.forEach.call(express, function (el) {
          if (!el.hasAttribute || !el.hasAttribute('data-centrepiece-blocked')) el.style.display = '';
        });
      }
      if (existing) existing.remove();
      return;
    }

    if (button) {
      button.setAttribute('data-hfd-blocked', '');
      button.disabled = true;
    }
    Array.prototype.forEach.call(express, function (el) { el.style.display = 'none'; });
    // alignCart is about to move the cart onto a valid date and reload: hold
    // checkout, but don't ask the customer to do anything.
    if (pending) {
      if (existing) existing.remove();
      return;
    }

    var names = [];
    bad.forEach(function (line) {
      var name = line.product_title || line.title || 'Your High Floral Design piece';
      if (names.indexOf(name) === -1) names.push(name);
    });
    var earliest = earliestForPostcode(postcode || bad[0].properties.Postcode, now);
    var html = '<p><strong>' + escapeHtml(joinNames(names)) + (names.length > 1 ? ' need' : ' needs') + ' a new delivery date.</strong> '
      + 'High Floral Design pieces need ' + businessDays() + ' business days\u2019 notice, so the date in your cart is too soon. '
      + 'The earliest delivery is ' + escapeHtml(describe(earliest)) + '.</p>'
      + '<a class="button button--secondary fb-hfd-cart-notice__button" href="' + REDATE_URL + '">Change delivery date</a>';

    var notice = existing;
    if (!notice) {
      notice = doc.createElement('div');
      notice.id = 'fbHfdCartNotice';
      notice.className = 'cart__ratio-notice fb-hfd-cart-notice';
      notice.setAttribute('role', 'alert');
      var ctas = doc.querySelector('.cart__ctas');
      if (ctas && ctas.parentNode) {
        ctas.parentNode.insertBefore(notice, ctas);
      } else if (button && button.parentNode) {
        button.parentNode.insertBefore(notice, button);
      } else {
        return;
      }
    }
    notice.innerHTML = html;
  }

  // Our own requests go through the fetch that existed before this file wrapped
  // it (below), so an alignment can never trigger another alignment.
  var nativeFetch = typeof root.fetch === 'function' ? root.fetch : null;
  var inFlight = null;
  var runAgain = false;
  var lastAlignFailed = false;
  var retried = false;

  /*
   * Read the cart, remember whether it holds an HFD piece, and move its dated
   * lines onto one date if it does. Resolves true if it changed anything. Never
   * rejects: this must not break the page, the popup or an add to cart.
   */
  function alignCart(now) {
    if (!nativeFetch) return Promise.resolve(false);
    if (inFlight) {
      runAgain = true;
      return inFlight;
    }

    lastAlignFailed = false;
    inFlight = nativeFetch.call(root, '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        cartHas = cartHoldsHfd(cart);
        var target = targetDate(cart, {
          iso: readCookie('ISODate'),
          label: readCookie('DeliveryDate'),
          postcode: readCookie('deliveryPostcode')
        }, now);
        // A stricter collection minimum or product day block also governs mixed carts.
        if (target && root.fbCentrepieceLeadTime && root.fbCentrepieceLeadTime.invalidDate(target.iso, readCookie('deliveryPostcode'), null, now)) target = null;
        var changes = target ? lineChanges(cart, target) : [];
        guardCartPage(cart, now, changes.length > 0);
        if (!target) return false;
        if (!target.fromCookie) {
          // Later adds use the cookie, so it has to agree with the cart.
          writeCookie('DeliveryDate', target.label, 7);
          writeCookie('ISODate', target.iso, 7);
          labelCards(root.document);
        }
        if (!changes.length) return false;
        // The cart drawer, when the page has one, is re-rendered from the last
        // change's response so it shows the new dates and line keys.
        var drawer = root.document && root.document.querySelector
          ? root.document.querySelector('cart-notification') : null;
        var drawerSections = (drawer && typeof drawer.refreshCart === 'function' && typeof drawer.getSectionsToRender === 'function')
          ? drawer.getSectionsToRender().map(function (section) { return section.id; }) : null;
        var lastState = null;
        // One at a time: each change replaces that line's key, never another's.
        return changes.reduce(function (chain, change, i) {
          return chain.then(function () {
            var body = change;
            if (drawerSections && i === changes.length - 1) {
              body = Object.assign({}, change, { sections: drawerSections, sections_url: root.location.pathname });
            }
            return nativeFetch.call(root, '/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body)
            }).then(function (res) {
              if (!res.ok) throw new Error('cart/change.js ' + res.status);
              return res.json();
            }).then(function (state) { lastState = state; });
          });
        }, Promise.resolve()).then(function () {
          if (drawerSections && lastState && lastState.sections) {
            try { drawer.refreshCart(lastState); } catch (e) { /* display only */ }
          }
          // The cart page is rendered on the server: reload it to show the dates.
          // The next alignment finds nothing to change, so this happens once.
          if (isCartPage() && typeof root.location.reload === 'function') {
            root.location.reload();
          }
          return true;
        });
      })
      .catch(function (err) {
        lastAlignFailed = true;
        if (root.console && root.console.warn) root.console.warn('HFD delivery date not applied to the cart:', err);
        // The cart page holds checkout until this succeeds: try once more.
        if (isCartPage() && !retried) {
          retried = true;
          setTimeout(function () { alignCart(now); }, 3000);
        }
        return false;
      })
      .then(function (changed) {
        inFlight = null;
        if (runAgain) {
          runAgain = false;
          return alignCart(now).then(function (again) { return changed || again; });
        }
        return changed;
      });
    return inFlight;
  }

  // "Change delivery date" from the cart. The HFD collection page is usually
  // hard-redirected to add the city filter (RedirectManager.checkAndRedirect in
  // assets/delivery-suburb-popup.js rebuilds the URL and drops every other query
  // parameter), so ?hfd_redate=1 alone never reaches the page that stays. The flag
  // is therefore also kept in sessionStorage for a few minutes, and cleared once a
  // date is saved, the customer adds something, or they browse elsewhere.
  var returnToCart = false;
  var REDATE_KEY = 'fbHfdRedate';
  var REDATE_TTL_MS = 10 * 60 * 1000;

  function redateFlag() {
    try {
      var at = parseInt(root.sessionStorage.getItem(REDATE_KEY), 10);
      return at > 0 && Date.now() - at < REDATE_TTL_MS;
    } catch (e) { return false; }
  }
  function setRedateFlag() {
    try { root.sessionStorage.setItem(REDATE_KEY, String(Date.now())); } catch (e) { /* the URL flag still works */ }
  }
  function clearRedateFlag() {
    returnToCart = false;
    try { root.sessionStorage.removeItem(REDATE_KEY); } catch (e) { /* nothing to clear */ }
  }

  /*
   * The popup saved a date with no add pending. Move the cart onto it, and go back
   * to the cart page if the customer came here to fix their date.
   */
  function dateSaved() {
    return alignCart().then(function (changed) {
      // A failed save stays here: the popup can be used again, and the cart
      // would only show the same notice.
      if (returnToCart && root.location && !lastAlignFailed) {
        clearRedateFlag();
        root.location.href = '/cart';
      }
      return changed;
    });
  }

  // ---- "Available from …" on HFD cards --------------------------------------

  /*
   * The label for HFD cards given the chosen date, or null when there is nothing
   * to say: no date chosen yet, or the date already meets the rule. Pure:
   * `chosen` is { iso, postcode }.
   */
  function availableLabel(chosen, now) {
    if (!chosen || !chosen.iso || !toYmd(chosen.iso)) return null;
    if (!isTooEarly(chosen.iso, chosen.postcode, now)) return null;
    return 'Available from ' + describe(earliestForPostcode(chosen.postcode, now));
  }

  /*
   * Fill every [data-fb-hfd-available] slot (snippets/fb-hfd-available.liquid,
   * rendered only for HFD products) from the date cookies. Writes only on change,
   * because the observer below watches the same subtree.
   */
  function labelCards(scope, now) {
    var within = scope || root.document;
    if (!within || typeof within.querySelectorAll !== 'function') return;
    var label = availableLabel({ iso: readCookie('ISODate'), postcode: readCookie('deliveryPostcode') }, now);
    var slots = within.querySelectorAll('[data-fb-hfd-available]');
    for (var i = 0; i < slots.length; i++) {
      var el = slots[i];
      if (label) {
        if (el.textContent !== label) el.textContent = label;
        if (el.hidden) el.hidden = false;
      } else if (!el.hidden) {
        el.hidden = true;
      }
    }
  }

  var api = {
    BUSINESS_DAYS: BUSINESS_DAYS,
    isHfdProduct: isHfdProduct,
    cartHasHfd: cartHasHfd,
    cartHoldsHfd: cartHoldsHfd,
    applies: applies,
    toYmd: toYmd,
    todayYmd: todayYmd,
    earliestYmd: earliestYmd,
    earliestForSettings: earliestForSettings,
    settingsFor: settingsFor,
    locationForPostcode: locationForPostcode,
    earliestForLocation: earliestForLocation,
    earliestForPostcode: earliestForPostcode,
    isTooEarly: isTooEarly,
    describe: describe,
    notice: notice,
    targetDate: targetDate,
    lineChanges: lineChanges,
    alignCart: alignCart,
    dateSaved: dateSaved,
    availableLabel: availableLabel,
    labelCards: labelCards,
    tooEarlyHfdLines: tooEarlyHfdLines,
    guardCartPage: guardCartPage,
    REDATE_URL: REDATE_URL
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  root.fbHfdLeadTime = api;

  if (!root.document || !root.addEventListener) return;

  // ---- watching the cart ---------------------------------------------------

  // Whether a cart request is worth a look: any add (it may be an HFD piece), and
  // any other change while the cart holds HFD (a removal clears the flag). A
  // change to a cart with no HFD piece can't need aligning, so costs nothing.
  function isLineMutation(url) {
    if (!url) return false;
    var path;
    try {
      path = new URL(String(url), root.location.origin).pathname;
    } catch (e) {
      path = String(url);
    }
    var m = /\/cart\/(add|change|update|clear)(\.js)?$/.exec(path);
    return !!m && (m[1] === 'add' || cartHasHfd());
  }

  var timer = null;
  function alignSoon(url) {
    // An add from the redate page means the customer moved on to shopping: don't
    // pull them back to the cart on a later, unrelated date change.
    if (url && /\/cart\/add/.test(String(url)) && returnToCart) clearRedateFlag();
    if (!productIds().length && !cartPageHeld()) return;
    clearTimeout(timer);
    timer = setTimeout(function () { alignCart(); }, 250);
  }
  api.alignSoon = alignSoon;

  // With no HFD products known there is nothing to watch, unless the cart page
  // was rendered held (see needsLoadCheck below), which still has to be lifted.

  if (nativeFetch) {
    root.fetch = function (input) {
      var url = typeof input === 'string' ? input : input && input.url;
      var result = nativeFetch.apply(this, arguments);
      if (isLineMutation(url)) {
        result.then(function (response) {
          if (response && response.ok) alignSoon(url);
        }, function () {});
      }
      return result;
    };
  }

  // The Delivery Date app adds with jQuery, which uses XHR.
  if (root.XMLHttpRequest) {
    var nativeOpen = root.XMLHttpRequest.prototype.open;
    var nativeSend = root.XMLHttpRequest.prototype.send;
    root.XMLHttpRequest.prototype.open = function (method, url) {
      this.__fbHfdLineMutation = isLineMutation(url);
      this.__fbHfdUrl = url;
      return nativeOpen.apply(this, arguments);
    };
    root.XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (xhr.__fbHfdLineMutation) {
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) alignSoon(xhr.__fbHfdUrl);
        });
      }
      return nativeSend.apply(this, arguments);
    };
  }

  /*
   * Product page guard. The Delivery Date app's Add to Cart button
   * (.custom_product__add__cart__button, bound with jQuery) adds with whatever
   * date is in the cookie and never runs product-form.js's gates. This runs in
   * the capture phase, before the app's handler, and stops a too-early add:
   * the date is cleared and the popup reopens at the date step, where the
   * calendar starts at the HFD date.
   */
  root.document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('.custom_product__add__cart__button')) return;
    if (!applies(null)) return;
    var postcode = readCookie('deliveryPostcode');
    var iso = readCookie('ISODate');
    if (!postcode || !iso || !isTooEarly(iso, postcode)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    writeCookie('DeliveryDate', '', -1);
    writeCookie('ISODate', '', -1);
    labelCards(root.document);
    if (typeof root.popupShowUnavailableToast === 'function') {
      root.popupShowUnavailableToast(null, notice(earliestForPostcode(postcode)));
    }
    if (typeof root.openPopupAtStep === 'function') {
      root.openPopupAtStep('date');
    }
  }, true);

  // The cart page re-renders its checkout button on quantity changes, which drops
  // the block; cart.js announces each re-render, so check again then.
  function cartPageHeld() {
    return isCartPage() && !!root.document.querySelector && !!root.document.querySelector('#checkout[data-hfd-blocked]');
  }
  root.document.addEventListener('cart:updated', function () {
    if (isCartPage() && (cartHasHfd() || cartPageHeld())) alignSoon();
  });

  /*
   * The drawer's Secure Checkout goes to /cart.
   * With an HFD piece in the cart, send it to /cart instead, where the date is
   * checked.
   */
  root.document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest || !target.closest('#cartCheckoutButton')) return;
    if (!cartHasHfd()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    root.location.href = '/cart';
  }, true);

  // Arrived from the cart's "Change delivery date": open the popup at the date
  // step, and return to the cart once a date is saved (dateSaved above). The
  // notice's link also sets the flag on click, before the page can redirect.
  root.document.addEventListener('click', function (event) {
    var target = event.target;
    if (target && target.closest && target.closest('.fb-hfd-cart-notice__button')) setRedateFlag();
  }, true);
  try {
    var params = new URLSearchParams(root.location.search || '');
    var fromUrl = params.get(REDATE_PARAM) === '1';
    if (fromUrl) {
      setRedateFlag();
      params.delete(REDATE_PARAM);
      var rest = params.toString();
      if (root.history && root.history.replaceState) {
        root.history.replaceState(root.history.state, '', root.location.pathname + (rest ? '?' + rest : '') + root.location.hash);
      }
    }
    if (fromUrl || redateFlag()) {
      if (config().pageIsHfdCollection === true) {
        returnToCart = true;
        var openDateStep = function () {
          if (typeof root.openPopupAtStep === 'function') root.openPopupAtStep('date');
        };
        if (root.document.readyState === 'loading') {
          root.document.addEventListener('DOMContentLoaded', openDateStep);
        } else {
          openDateStep();
        }
      } else if (!isCartPage()) {
        // Browsed somewhere else instead: forget it.
        clearRedateFlag();
      }
    }
  } catch (e) { /* the page still works; the customer can use the date pill */ }

  // "Available from …": label now, again whenever the date may have changed
  // (the popup, the header pills, a tab coming back), and on any cards that
  // arrive later (grid filter swaps, recommendations, quick view).
  function relabel() { labelCards(root.document); }
  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', relabel);
  } else {
    relabel();
  }
  root.addEventListener('deliveryInfoUpdated', relabel);
  root.document.addEventListener('fb:delivery-location-changed', relabel);
  root.document.addEventListener('visibilitychange', function () {
    if (!root.document.hidden) relabel();
  });
  // The label depends on the postcode's location settings, which the Delivery
  // Date app's block and other deferred scripts define. If they are not here yet,
  // label again once they are (the same wait assets/fb-suburb-grid.js does), and
  // once more when the page has finished loading.
  if (typeof root.fbResolvePostcode !== 'function' && typeof setInterval === 'function') {
    var resolverTries = 0;
    var resolverTimer = setInterval(function () {
      resolverTries++;
      if (typeof root.fbResolvePostcode === 'function' || resolverTries >= 20) {
        clearInterval(resolverTimer);
        relabel();
      }
    }, 250);
  }
  root.addEventListener('load', relabel);

  // Cards that arrive later (grid filter swaps, recommendations, quick view).
  // Only on a store with HFD products, and each added node is checked with a
  // cheap class lookup, so other pages pay next to nothing.
  if (productIds().length && typeof root.MutationObserver === 'function' && root.document.documentElement) {
    var relabelQueued = false;
    var hasSlot = function (node) {
      if (node.nodeType !== 1) return false;
      if (node.classList && node.classList.contains('fb-hfd-available')) return true;
      return !!(node.getElementsByClassName && node.getElementsByClassName('fb-hfd-available').length);
    };
    new root.MutationObserver(function (records) {
      if (relabelQueued) return;
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        for (var a = 0; a < added.length; a++) {
          if (hasSlot(added[a])) {
            relabelQueued = true;
            (root.requestAnimationFrame || setTimeout)(function () { relabelQueued = false; relabel(); });
            return;
          }
        }
      }
    }).observe(root.document.documentElement, { childList: true, subtree: true });
  }

  // A cart rendered with an HFD piece may carry lines from before the rule, or
  // from another tab. Put it right on load (and, on the cart page, guard it).
  // The cart page renders checkout held whenever Liquid sees an HFD piece; run
  // even if this file's product list missed it, or the hold would never lift.
  var needsLoadCheck = function () { return cartHasHfd() || cartPageHeld(); };
  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', function () { if (needsLoadCheck()) alignCart(); });
  } else if (needsLoadCheck()) {
    alignCart();
  }
})(typeof window !== 'undefined' ? window : globalThis);
